import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../lib/i18n';
import { investmentsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import {
  TrendingUp, TrendingDown, Plus, Trash2, Edit2,
  PieChart, BarChart3, Briefcase, Bitcoin, Building,
  Gem, DollarSign, RefreshCw, Zap, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { cn, formatCurrency } from '../lib/utils';
import { PieChart as RechartsRie, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const assetTypes = [
  { value: 'stock', label: 'Acciones', icon: TrendingUp, color: '#10B981' },
  { value: 'etf', label: 'ETF', icon: BarChart3, color: '#3B82F6' },
  { value: 'crypto', label: 'Cripto', icon: Bitcoin, color: '#F59E0B' },
  { value: 'bond', label: 'Bonos', icon: Briefcase, color: '#8B5CF6' },
  { value: 'real_estate', label: 'Inmuebles', icon: Building, color: '#EC4899' },
  { value: 'commodity', label: 'Materias Primas', icon: Gem, color: '#6366F1' },
  { value: 'cash', label: 'Efectivo', icon: DollarSign, color: '#64748B' },
];

const Investments = () => {
  const { language, user } = useAuth();
  const t = useTranslation(language);
  const currency = user?.currency || 'EUR';

  const [investments, setInvestments] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingPrices, setUpdatingPrices] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [form, setForm] = useState({
    name: '',
    symbol: '',
    asset_type: 'stock',
    quantity: '',
    purchase_price: '',
    current_price: '',
    purchase_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const handleUpdatePrices = async () => {
    setUpdatingPrices(true);
    try {
      const res = await investmentsAPI.updateAllPrices();
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error('Error al actualizar precios');
    } finally {
      setUpdatingPrices(false);
    }
  };

  const handleSearch = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const res = await investmentsAPI.searchMarket(query);
      setSearchResults(res.data);
    } catch (error) {
      console.error('Search error');
    } finally {
      setSearching(false);
    }
  };

  const selectFromSearch = (result) => {
    setForm({
      ...form,
      name: result.name,
      symbol: result.symbol,
      asset_type: result.type === 'equity' ? 'stock' :
        result.type === 'etf' ? 'etf' :
          result.type === 'crypto' ? 'crypto' : 'stock'
    });
    setSearchResults([]);
    setSearchQuery('');
  };

  const loadData = async () => {
    try {
      const [invRes, portRes] = await Promise.all([
        investmentsAPI.getAll(),
        investmentsAPI.getPortfolio()
      ]);
      setInvestments(invRes.data);
      setPortfolio(portRes.data);
    } catch (error) {
      toast.error('Error al cargar inversiones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const data = {
        name: form.name,
        symbol: form.symbol?.trim() || null,
        asset_type: form.asset_type,
        quantity: Number(form.quantity),
        purchase_price: Number(form.purchase_price),
        current_price: form.current_price ? Number(form.current_price) : null,
        purchase_date: form.purchase_date,
        notes: form.notes?.trim() || null
      };

      if (editingId) {
        await investmentsAPI.update(editingId, {
          current_price: data.current_price,
          quantity: data.quantity,
          notes: data.notes
        });
        toast.success('Inversión actualizada');
      } else {
        await investmentsAPI.create(data);
        toast.success('Inversión añadida');
      }

      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta inversión?')) return;

    try {
      await investmentsAPI.delete(id);
      toast.success('Inversión eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEdit = (inv) => {
    setForm({
      name: inv.name,
      symbol: inv.symbol || '',
      asset_type: inv.asset_type,
      quantity: inv.quantity.toString(),
      purchase_price: inv.purchase_price.toString(),
      current_price: inv.current_price.toString(),
      purchase_date: inv.purchase_date.split('T')[0],
      notes: inv.notes || ''
    });
    setEditingId(inv.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setForm({
      name: '',
      symbol: '',
      asset_type: 'stock',
      quantity: '',
      purchase_price: '',
      current_price: '',
      purchase_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setEditingId(null);
  };

  const getAssetIcon = (type) => {
    const asset = assetTypes.find(a => a.value === type);
    return asset?.icon || TrendingUp;
  };

  const getAssetColor = (type) => {
    const asset = assetTypes.find(a => a.value === type);
    return asset?.color || '#64748B';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const pieData = portfolio?.by_asset_type?.map(item => ({
    name: assetTypes.find(a => a.value === item.type)?.label || item.type,
    value: item.value,
    color: getAssetColor(item.type)
  })) || [];

  return (
    <div className="space-y-6" data-testid="investments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl lg:text-3xl text-foreground tracking-tight">
            Inversiones
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gestiona tu portafolio de inversiones
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleUpdatePrices}
            disabled={updatingPrices}
            className="rounded-sm"
            data-testid="update-prices-btn"
          >
            {updatingPrices ? (
              <RefreshCw size={16} className="mr-2 animate-spin" />
            ) : (
              <Zap size={16} className="mr-2" />
            )}
            {updatingPrices ? 'Actualizando...' : 'Actualizar Precios'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="add-investment-btn">
                <Plus size={18} className="mr-2" />
                Nueva Inversión
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Editar Inversión' : 'Nueva Inversión'}</DialogTitle>
              </DialogHeader>

              {/* Search bar for new investments */}
              {!editingId && (
                <div className="space-y-2">
                  <Label>Buscar activo</Label>
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        handleSearch(e.target.value);
                      }}
                      placeholder="Buscar por nombre o símbolo..."
                      className="rounded-sm pl-9"
                    />
                  </div>
                  {searchResults.length > 0 && (
                    <div className="max-h-40 overflow-y-auto border border-border rounded-sm">
                      {searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          onClick={() => selectFromSearch(result)}
                          className="w-full flex items-center justify-between p-2 hover:bg-muted text-left"
                        >
                          <div>
                            <span className="font-mono text-sm font-medium">{result.symbol}</span>
                            <span className="text-xs text-muted-foreground ml-2">{result.name}</span>
                          </div>
                          <span className="text-xs text-muted-foreground">{result.type}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nombre</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Ej: Apple Inc."
                      className="rounded-sm"
                      disabled={!!editingId}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Símbolo</Label>
                    <Input
                      value={form.symbol}
                      onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
                      placeholder="Ej: AAPL"
                      className="rounded-sm"
                      disabled={!!editingId}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Tipo de Activo</Label>
                  <Select
                    value={form.asset_type}
                    onValueChange={(v) => setForm({ ...form, asset_type: v })}
                    disabled={!!editingId}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {assetTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <div className="flex items-center gap-2">
                            <type.icon size={16} style={{ color: type.color }} />
                            {type.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Cantidad</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio Compra</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.purchase_price}
                      onChange={(e) => setForm({ ...form, purchase_price: e.target.value })}
                      className="rounded-sm"
                      disabled={!!editingId}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Precio Actual</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.current_price}
                      onChange={(e) => setForm({ ...form, current_price: e.target.value })}
                      className="rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Fecha de Compra</Label>
                  <Input
                    type="date"
                    value={form.purchase_date}
                    onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                    className="rounded-sm"
                    disabled={!!editingId}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Notas</Label>
                  <Input
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Notas adicionales..."
                    className="rounded-sm"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
                  Cancelar
                </Button>
                <Button onClick={handleSubmit} className="bg-primary hover:bg-primary/90 rounded-sm">
                  {editingId ? 'Actualizar' : 'Añadir'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* AI Strategy Card */}
      {portfolio?.ai_strategy && (
        <Card className="border-border rounded-sm shadow-none bg-gradient-to-r from-emerald-900/10 to-transparent">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className={cn(
                "p-2 rounded-sm text-white",
                portfolio.ai_strategy.type === "warning" ? "bg-amber-500" : "bg-emerald-600"
              )}>
                <Zap size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  {portfolio.ai_strategy.title}
                  <span className="text-[10px] uppercase tracking-widest text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded-sm">
                    AI Insight
                  </span>
                </h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-3xl leading-relaxed">
                  {portfolio.ai_strategy.description}
                </p>
              </div>
              {portfolio.ai_strategy.action_text && (
                <Button variant="outline" size="sm" className="hidden sm:flex rounded-sm">
                  {portfolio.ai_strategy.action_text}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Portfolio Summary */}
      {portfolio && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-border rounded-sm shadow-none">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Invertido</p>
              <p className="font-mono text-xl font-bold text-foreground">
                {formatCurrency(portfolio.total_invested, currency)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border rounded-sm shadow-none">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Valor Actual</p>
              <p className="font-mono text-xl font-bold text-foreground">
                {formatCurrency(portfolio.current_value, currency)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border rounded-sm shadow-none">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Ganancia/Pérdida</p>
              <p className={cn(
                "font-mono text-xl font-bold",
                portfolio.total_profit_loss >= 0 ? "text-emerald-600" : "text-red-600"
              )}>
                {portfolio.total_profit_loss >= 0 ? '+' : ''}
                {formatCurrency(portfolio.total_profit_loss, currency)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-border rounded-sm shadow-none">
            <CardContent className="p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Rentabilidad</p>
              <div className="flex items-center gap-2">
                {portfolio.total_profit_loss_percent >= 0 ? (
                  <TrendingUp className="text-emerald-600" size={20} />
                ) : (
                  <TrendingDown className="text-red-600" size={20} />
                )}
                <p className={cn(
                  "font-mono text-xl font-bold",
                  portfolio.total_profit_loss_percent >= 0 ? "text-emerald-600" : "text-red-600"
                )}>
                  {portfolio.total_profit_loss_percent >= 0 ? '+' : ''}
                  {portfolio.total_profit_loss_percent.toFixed(2)}%
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts and Allocation */}
      {portfolio && pieData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-border rounded-sm shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-semibold">Distribución por Tipo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartsRie>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => formatCurrency(value, currency)}
                      contentStyle={{ borderRadius: '2px' }}
                    />
                    <Legend />
                  </RechartsRie>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border rounded-sm shadow-none">
            <CardHeader>
              <CardTitle className="text-sm font-semibold">Mejores/Peores Rendimientos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground mb-2">Top Performers</p>
                {portfolio.top_performers?.slice(0, 3).map((inv, i) => (
                  <div key={i} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                    <span className="text-sm text-foreground">{inv.symbol || inv.name}</span>
                    <span className="font-mono text-sm text-emerald-600">
                      +{inv.profit_loss_percent.toFixed(2)}%
                    </span>
                  </div>
                ))}
              </div>

              {portfolio.worst_performers?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Peores Rendimientos</p>
                  {portfolio.worst_performers?.slice(0, 3).map((inv, i) => (
                    <div key={i} className="flex justify-between items-center py-2 border-b border-border last:border-0">
                      <span className="text-sm text-foreground">{inv.symbol || inv.name}</span>
                      <span className="font-mono text-sm text-red-600">
                        {inv.profit_loss_percent.toFixed(2)}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Investments List */}
      <Card className="border-border rounded-sm shadow-none">
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Mis Inversiones</CardTitle>
        </CardHeader>
        <CardContent>
          {investments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No tienes inversiones registradas</p>
              <p className="text-sm">Añade tu primera inversión para comenzar</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 text-xs uppercase tracking-widest text-muted-foreground">Activo</th>
                    <th className="text-right py-3 px-2 text-xs uppercase tracking-widest text-muted-foreground">Cantidad</th>
                    <th className="text-right py-3 px-2 text-xs uppercase tracking-widest text-muted-foreground">Precio Compra</th>
                    <th className="text-right py-3 px-2 text-xs uppercase tracking-widest text-muted-foreground">Precio Actual</th>
                    <th className="text-right py-3 px-2 text-xs uppercase tracking-widest text-muted-foreground">Valor</th>
                    <th className="text-right py-3 px-2 text-xs uppercase tracking-widest text-muted-foreground">P/L</th>
                    <th className="text-right py-3 px-2 text-xs uppercase tracking-widest text-muted-foreground">%</th>
                    <th className="text-right py-3 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {investments.map(inv => {
                    const Icon = getAssetIcon(inv.asset_type);
                    const color = getAssetColor(inv.asset_type);

                    return (
                      <tr key={inv.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-3">
                            <div
                              className="w-8 h-8 rounded-sm flex items-center justify-center"
                              style={{ backgroundColor: `${color}20` }}
                            >
                              <Icon size={16} style={{ color }} />
                            </div>
                            <div>
                              <p className="font-medium text-foreground text-sm">{inv.name}</p>
                              {inv.symbol && (
                                <p className="text-xs text-muted-foreground">{inv.symbol}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-sm">{inv.quantity}</td>
                        <td className="py-3 px-2 text-right font-mono text-sm">
                          {formatCurrency(inv.purchase_price, currency)}
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-sm">
                          {formatCurrency(inv.current_price, currency)}
                        </td>
                        <td className="py-3 px-2 text-right font-mono text-sm font-medium">
                          {formatCurrency(inv.current_value, currency)}
                        </td>
                        <td className={cn(
                          "py-3 px-2 text-right font-mono text-sm",
                          inv.profit_loss >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {inv.profit_loss >= 0 ? '+' : ''}
                          {formatCurrency(inv.profit_loss, currency)}
                        </td>
                        <td className={cn(
                          "py-3 px-2 text-right font-mono text-sm",
                          inv.profit_loss_percent >= 0 ? "text-emerald-600" : "text-red-600"
                        )}>
                          {inv.profit_loss_percent >= 0 ? '+' : ''}
                          {inv.profit_loss_percent.toFixed(2)}%
                        </td>
                        <td className="py-3 px-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(inv)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit2 size={14} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(inv.id)}
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Investments;
